/**
 * PageTransition - Wrapper component for smooth page transitions
 * Uses Framer Motion for fade and slide animations
 */
import { motion } from 'framer-motion';

// Animation variants for different transition styles
const variants = {
  // Default fade + slight slide up
  default: {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 }
  },
  // Fade only (for modals/overlays)
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  },
  // Slide from right (for detail pages)
  slideRight: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  },
  // Slide from left (for back navigation)
  slideLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 20 }
  },
  // Scale up (for emphasis)
  scale: {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.98 }
  }
};

// Transition settings
const transition = {
  duration: 0.25,
  ease: [0.25, 0.1, 0.25, 1] // Custom easing curve
};

export function PageTransition({ 
  children, 
  variant = 'default',
  className = '',
  ...props 
}) {
  const selectedVariant = variants[variant] || variants.default;

  return (
    <motion.div
      initial={selectedVariant.initial}
      animate={selectedVariant.animate}
      exit={selectedVariant.exit}
      transition={transition}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// Pre-configured transitions for common use cases
export function FadeTransition({ children, ...props }) {
  return <PageTransition variant="fade" {...props}>{children}</PageTransition>;
}

export function SlideTransition({ children, direction = 'right', ...props }) {
  const variant = direction === 'left' ? 'slideLeft' : 'slideRight';
  return <PageTransition variant={variant} {...props}>{children}</PageTransition>;
}

export function ScaleTransition({ children, ...props }) {
  return <PageTransition variant="scale" {...props}>{children}</PageTransition>;
}

export default PageTransition;
